// A hook for saving user state changes with debounce and optimistic updates.
// Used in the library sidebar to save user preferences and moderation settings.

import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useLocation } from 'react-router';
import { toast } from 'sonner';

import { useAuth } from './useAuth';
import { updateUser } from '../transport/api/protectedApi';
import type {
  UserAuthFields,
  UserFieldsUpdate,
} from '@airhead-games/shared/types';

interface UseUserSaveOptions<TState> {
  value: TState;
  setValue: Dispatch<SetStateAction<TState>>;
  toUpdate: (value: TState) => Partial<UserFieldsUpdate>;
  fromUser: (user: UserAuthFields) => TState;
  debounceMs?: number;
  errorMessage?: string;
  onSaved?: (user: UserAuthFields, savedState: TState) => void;
}

const isAbortError = (error: unknown) =>
  error instanceof DOMException && error.name === 'AbortError';

export function useUserSave<TState>({
  value,
  setValue,
  toUpdate,
  fromUser,
  debounceMs = 300,
  errorMessage = 'Unable to save your changes.',
  onSaved,
}: UseUserSaveOptions<TState>) {
  const location = useLocation();
  const { user, setUser } = useAuth();
  const valueRef = useRef(value);
  const committedRef = useRef(value);
  const toUpdateRef = useRef(toUpdate);
  const fromUserRef = useRef(fromUser);
  const onSavedRef = useRef(onSaved);
  const errorMessageRef = useRef(errorMessage);
  const timeoutRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const dirtyRef = useRef(false);
  const routeKeyRef = useRef(`${location.pathname}${location.search}`);
  const isMountedRef = useRef(true);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    // Keep the latest mapping callbacks in refs so cleanup-only effects do not
    // re-fire with stale state every time callers create new inline functions.
    toUpdateRef.current = toUpdate;
    fromUserRef.current = fromUser;
    onSavedRef.current = onSaved;
    errorMessageRef.current = errorMessage;
  }, [errorMessage, fromUser, onSaved, toUpdate]);

  useEffect(() => {
    if (!user || dirtyRef.current) {
      return;
    }

    const committedState = fromUserRef.current(user);
    committedRef.current = committedState;
    setValue(committedState);
  }, [setValue, user]);

  const flush = useCallback(async () => {
    if (!dirtyRef.current) {
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await updateUser(toUpdateRef.current(valueRef.current), {
        signal: controller.signal,
      });
      const updatedUser = response.data?.user;

      if (!updatedUser) {
        throw new Error('Updated user payload missing from response.');
      }

      const savedState = fromUserRef.current(updatedUser);
      committedRef.current = savedState;
      dirtyRef.current = false;
      setUser(updatedUser);

      if (isMountedRef.current) {
        setValue(savedState);
        onSavedRef.current?.(updatedUser, savedState);
      }
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }

      dirtyRef.current = false;

      if (isMountedRef.current) {
        setValue(committedRef.current);
      }
      toast.error(errorMessageRef.current);
    }
  }, [setUser, setValue]);

  const markDirty = useCallback(() => {
    dirtyRef.current = true;
    abortRef.current?.abort();

    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      void flush();
    }, debounceMs);
  }, [debounceMs, flush]);

  useEffect(() => {
    const nextRouteKey = `${location.pathname}${location.search}`;
    if (routeKeyRef.current !== nextRouteKey) {
      routeKeyRef.current = nextRouteKey;
      void flush();
    }
  }, [flush, location.pathname, location.search]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
      abortRef.current?.abort();
      void flush();
    };
  }, [flush]);

  return { markDirty, flush };
}
